import { checkoutAccommodationGroupingKey } from 'checkout/selectors/view/accommodation'
import { createSelector } from 'reselect'
import { groupBy, nonNullable, max } from 'lib/array/arrayUtils'
import { countOccupants, countTravellersAsOccupants, getOccupanciesFromReservations, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { getInsuranceEnabledCheckoutItems } from '../view/insurance'
import { isStandaloneFlights } from '../view/flights'
import { isStandaloneExperience } from 'selectors/checkoutSelectors'
import getInsuranceFlightOccupants from './getInsuranceFlightOccupants'
import getExperienceParticipantOccupants from './getExperienceParticipantOccupants'
import getFlightDetailsByOrderItems from 'lib/flights/getFlightDetailsByOrderItems'
import { isAccommodationItem } from 'lib/checkout/checkoutUtils'
import { mergeOccupanciesByMaxPerType } from 'lib/insurance/insuranceOccupants'
import { getBookingProtectionOccupantsFromCartItems, getExperienceTicketCountFallbackOccupants } from './bookingProtectionOccupants'

/**
 * Leaf selectors for insurance quote occupancy / travellers.
 * Kept out of request/insurance.ts so getCheckoutInsuranceQuoteParams can import them
 * without re-entering insurance.ts mid-init through the bookingProtection ↔ totals cycle.
 */

const getTotalOccupancyCount = (occupancy: App.Occupants): number => {
  return sumUpOccupancies([occupancy])
}

const getInsuranceOccupantsByAccommodationGroup = createSelector(
  (state: App.State) => getInsuranceEnabledCheckoutItems(state),
  (items) => {
    const accommodationItems = items.filter(item => isAccommodationItem(item))
    if (accommodationItems.length > 0) {
      const itemMap = groupBy(accommodationItems, checkoutAccommodationGroupingKey)

      return Array.from(itemMap.values()).map((itemList) => {
        const occupants = nonNullable(itemList.map(item => item.occupancy))
        return countOccupants(occupants)
      })
    }
  },
)

export const getOccupantsFromCartItems = createSelector(
  (state: App.State) => getInsuranceOccupantsByAccommodationGroup(state),
  (state: App.State) => isStandaloneFlights(state),
  (state: App.State) => getInsuranceFlightOccupants(state),
  (state: App.State) => isStandaloneExperience(state),
  (state: App.State) => getExperienceTicketCountFallbackOccupants(state),
  (state: App.State) => getExperienceParticipantOccupants(state),
  (accommodationOccupants, isStandaloneFlight, flightOccupancies, isStandaloneExperience, experienceFallbackOccupants, experienceParticipantOccupants) => {
    if (isStandaloneExperience) {
      /**
       * Standalone experiences: quote occupancy must count the same expanded
       * participants (participantsPerUnit, adult/child fares) as the traveller
       * forms the order submits, or the provider rejects the booking after
       * payment. Raw ticket counts are only a stopgap until the experience view
       * loads — quotes are cover/date-gated on the same view data, so they are
       * never fetched from the fallback.
       */
      return experienceParticipantOccupants ?? experienceFallbackOccupants
    }

    const baseOccupants = (() => {
      /**
       * If both flight and accommodation occupants exist, merge them using max-per-type algorithm.
       * This ensures consistent traveller counts between quote fetching and order submission.
       *
       * Example:
       * - Hotel room: 2 adults, 1 child (total: 3)
       * - Flight: 3 adults, 0 children (total: 3)
       * - Result: 3 adults, 1 child = 4 travellers
       *
       * Previously, this would pick ONE source with the highest total, which could cause
       * mismatches with the form schema that uses max-per-type algorithm.
       */
      if (flightOccupancies && accommodationOccupants) {
        return mergeOccupanciesByMaxPerType(flightOccupancies, accommodationOccupants)
      }

      /**
       * We sell cfmr for standalone flights
       * In this case, we need to get the occupants from the flight with highest occupancy
       */
      if (isStandaloneFlight && flightOccupancies) {
        return max(flightOccupancies, getTotalOccupancyCount)
      }

      if (flightOccupancies) {
        return mergeOccupanciesByMaxPerType(flightOccupancies)
      }

      if (accommodationOccupants) {
        return max(accommodationOccupants, getTotalOccupancyCount)
      }
    })()

    /**
     * Mixed carts (accommodation/flights + experiences): the traveller form schema also
     * generates participant forms for experience tickets, and svc-traveller unions forms
     * max-per-type across items. Merge the experience participants into the quote
     * occupancy the same way so the order never submits more travellers than the quote
     * was fetched for.
     */
    if (baseOccupants && experienceParticipantOccupants) {
      return mergeOccupanciesByMaxPerType([baseOccupants], [experienceParticipantOccupants])
    }

    return baseOccupants
  },
)

export const getOccupantsFromExistingOrder = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.orders.flightDetails,
  (state: App.State) => state.experience.bookingDetails,
  (order, flightDetails, experiencesBookingDetails): App.Occupants | undefined => {
    if (order) {
      if (order.items.length) {
        // Exclude cancelled items so their reservations aren't double-counted alongside the
        // active ones (e.g. a date change leaves a cancelled item next to its replacement).
        const activeItems = order.items.filter(item => item.status !== 'cancelled')
        const reservations = nonNullable(activeItems.map(item => item.reservation))
        return getOccupanciesFromReservations(...reservations)
      } else if (order.bedbankItems.length) {
        const activeBedbankItems = order.bedbankItems.filter(item => item.status !== 'cancelled')
        return countOccupants(activeBedbankItems.flatMap(item => item.rooms.map(room => room.occupancy)))
      } else if (order.tourItems.length) {
        const completedItems = order.tourItems.filter(item => item.status === 'completed')
        return countTravellersAsOccupants(completedItems.flatMap(items => items.tour.travellers))
      } else if (order.cruiseItems.length) {
        const completedItems = order.cruiseItems.filter(item => item.status === 'completed')
        return countTravellersAsOccupants(completedItems.flatMap(items => items.passengers))
      } else if (order.flightItems.length) {
        if (!Object.keys(flightDetails).length) return

        const flightDetailsByOrderItems = getFlightDetailsByOrderItems(order, flightDetails)
        if (!flightDetailsByOrderItems) return

        const flightItemsIds = order.flightItems.map(item => item.id)

        const travellers = flightItemsIds.reduce<Array<App.FlightTraveller>>((acc, curr) => {
          const currentFlightDetails: App.FlightOrderItemDetails | undefined = flightDetailsByOrderItems[curr]
          const travellers = currentFlightDetails?.travellers || []
          return [...acc, ...travellers]
        }, [])

        return countTravellersAsOccupants(travellers)
      } else if (order.experienceItems.length) {
        if (!experiencesBookingDetails) return
        const totalTickets = Object.values(experiencesBookingDetails).filter(booking => booking.orderId === order.id).length

        return {
          adults: totalTickets,
          children: 0,
          infants: 0,
          teenagers: 0,
          childrenAge: [],
        }
      }
    }
  },
)

const EmptyOccupants: App.Occupants = { adults: 0 }

function formatInsuranceTravellers(travellers: App.Occupants | undefined): App.Occupants {
  const formattedTravellers = travellers ?? EmptyOccupants
  return {
    adults: formattedTravellers.adults,
    children: (formattedTravellers.teenagers || 0) + (formattedTravellers.children || 0),
    // Note: no teenagers field - they're counted as children for insurance
    infants: formattedTravellers.infants,
    childrenAge: formattedTravellers.childrenAge,
    childrenBirthDate: formattedTravellers.childrenBirthDate,
  }
}

export const getCheckoutInsuranceTravellers = createSelector(
  (state: App.State) => getOccupantsFromExistingOrder(state),
  (state: App.State) => getOccupantsFromCartItems(state),
  (orderTravellers, cartTravellers): App.Occupants => formatInsuranceTravellers(cartTravellers ?? orderTravellers),
)

export const getCheckoutInsuranceBookingProtectionTravellers = createSelector(
  (state: App.State) => getBookingProtectionOccupantsFromCartItems(state),
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (bookingProtectionTravellers, fallbackTravellers): App.Occupants => formatInsuranceTravellers(bookingProtectionTravellers ?? fallbackTravellers),
)

/**
 * Returns the total number of travellers that will be used for insurance quotes.
 * This count is derived using the max-per-type algorithm that merges occupancies
 * from all sources (flights, accommodation, etc.).
 *
 * Use this selector to detect when traveller count changes and invalidate quotes.
 */
export const getCheckoutInsuranceTravellerCount = createSelector(
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (travellers): number => {
    if (!travellers) return 0
    return (travellers.adults ?? 0) +
      (travellers.children ?? 0) +
      (travellers.infants ?? 0) +
      (travellers.teenagers ?? 0)
  },
)

/**
 * Returns a key representing the current traveller configuration for insurance.
 * This includes both the counts per type, allowing detection of changes that
 * would require quote invalidation.
 */
export const getCheckoutInsuranceTravellerKey = createSelector(
  (state: App.State) => getCheckoutInsuranceTravellers(state),
  (travellers): string => {
    if (!travellers) return '0-0-0'
    return `${travellers.adults ?? 0}-${travellers.children ?? 0}-${travellers.infants ?? 0}`
  },
)
